import { starteFortschritt, schritt, vollendeFortschritt, beendeFortschritt } from '@theme/dp-kauf-fortschritt';
import { beobachteAlleRaender } from '@theme/dp-feldrand';

/**
 * Leere Angaben zur Personalisierung nicht mitschicken.
 *
 * Die Felder sind freiwillig - wer keinen Spitznamen aufgedruckt haben will,
 * laesst das Feld leer. Ohne Zutun stuende die Angabe trotzdem in der
 * Bestellung, nur eben ohne Inhalt.
 *
 * Gearbeitet wird ueber den Namen des Feldes: was keinen Namen traegt, wird
 * nicht uebertragen. Der Name wandert dafuer in ein Datenattribut und kommt
 * zurueck, sobald etwas darin steht.
 *
 * Ein Abschalten des Feldes waere der naheliegende Weg gewesen, aber ein
 * abgeschaltetes Feld laesst sich nicht mehr beschreiben - und es zu spaet
 * abzuschalten hilft nicht: das Theme liest das Formular bereits beim Klick auf
 * den Knopf aus, nicht erst beim Absenden.
 */
/**
 * Ist das ein Feld zur Personalisierung? Einzeilig, mehrzeilig oder Datei.
 *
 * @param {EventTarget | null} was
 * @returns {boolean}
 */
function istPersoFeld(was) {
  if (!(was instanceof HTMLInputElement) && !(was instanceof HTMLTextAreaElement)) return false;
  return was.matches('[data-dp-perso]');
}

/**
 * Ob in einem Feld nichts steht - je nach Art des Feldes.
 *
 * Beim Dateifeld zaehlt die Auswahl, beim Ankreuzfeld der Haken, sonst der
 * Text ohne Randleerzeichen.
 *
 * @param {HTMLInputElement | HTMLTextAreaElement} feld
 */
function istLeer(feld) {
  if (feld.type === 'checkbox') return !feld.checked;
  if (feld.files) return feld.files.length === 0;
  return String(feld.value || '').trim() === '';
}

/**
 * Ob ein Feld eine Pflichtangabe ist.
 *
 * @param {HTMLInputElement | HTMLTextAreaElement} feld
 */
function istPflicht(feld) {
  return feld.required;
}

function normalisiere(feld) {
  // Beim Dateifeld zaehlt, ob etwas ausgewaehlt ist, nicht der Textwert.
  const leer = feld.files ? feld.files.length === 0 : feld.value.trim() === '';

  if (leer && feld.name) {
    feld.dataset.dpName = feld.name;
    feld.removeAttribute('name');
    return;
  }

  if (!leer && !feld.name && feld.dataset.dpName) {
    feld.name = feld.dataset.dpName;
  }
}

function alleFelder() {
  for (const feld of document.querySelectorAll('[data-dp-perso]')) normalisiere(feld);
  // Die Namen auf dem Rand, die der Server mitgeliefert hat (neu gezeichnete Felder eingeschlossen).
  beobachteAlleRaender();
}

// Wer ein Feld ansteuert, ohne zu klicken (Tabulator), bekommt die Messung vor der ersten Eingabe.
document.addEventListener('focusin', (ereignis) => {
  if (istPersoFeld(ereignis.target)) beobachteAlleRaender();
});
/*
 * Auch fuer dp-veredelung.js: Felder, deren Wert ein Skript setzt (Einfaerbung,
 * Auswahl, Haken, Farbe), bekommen keinen input - ihr Name kam erst beim
 * naechsten Klick zurueck. Wer das Formular liest, ohne dass geklickt wurde
 * (die Probe in IMPRINT, die Gruppen der Angaben), ruft das hier vorher auf
 * (Jimmi, Oktober 2026: die Einfaerbung fehlte im Personalisierungsoverlay).
 */
window.dpPersoNamen = alleFelder;

// Waehrend des Tippens - so ist der Zustand immer der aktuelle.
/**
 * Passt die Hoehe eines mehrzeiligen Feldes seinem Inhalt an.
 *
 * Ein Textfeld mit mehr Text als Platz bekommt einen eigenen Scrollbereich.
 * Auf dem Handy faengt der dann die Wischgeste ab: man will das Kauffenster
 * bewegen und bewegt stattdessen den Inhalt des Feldes - oder gar nichts, weil
 * darin nichts zu scrollen ist. Waechst das Feld mit, entsteht dieser Bereich
 * erst gar nicht.
 *
 * @param {HTMLTextAreaElement} feld
 */
function passeHoeheAn(feld) {
  // Noch nicht zu sehen (Reiter zu): dann gibt es nichts zu messen - das Oeffnen holt es nach.
  if (!feld.getClientRects().length) return;
  /*
   * Die Rahmenstaerke gehoert dazu: das Feld rechnet seine Hoehe von Kante zu
   * Kante, die gemessene Inhaltshoehe kennt aber nur den Innenraum. Ohne die
   * zwei Pixel bliebe ein schmaler Rest zum Scrollen uebrig - genau der, den
   * es hier zu vermeiden gilt.
   */
  const stil = window.getComputedStyle(feld);
  const rahmen = parseFloat(stil.borderTopWidth) + parseFloat(stil.borderBottomWidth);

  feld.style.height = 'auto';
  feld.style.height = feld.scrollHeight + rahmen + 'px';

  /*
   * Ab seinem Hoechstmass (max-height im Stilblatt) waechst das Feld nicht
   * weiter - dann rollt es in sich (Jimmi, September 2026). Vorher stand der
   * Text ab der achten Zeile unsichtbar und unerreichbar darunter. Den
   * Rollbereich gibt es nur, solange der Text wirklich nicht hineinpasst; ist
   * er am Ende, geht die Wischgeste an das Kauffenster weiter.
   */
  feld.classList.toggle('dp-perso__eingabe--rollt', feld.scrollHeight > feld.clientHeight + 1);
  pflegeRollrand(feld);
}

/**
 * Weicher Rand oben und unten im rollenden Feld - an der Kante, hinter der
 * noch Text liegt, wie bei den Farb- und Schriftreihen (Jimmi, September
 * 2026). Die Huelle bekommt die Klasse mit: Solange das Feld rollt, liegt
 * seine Flaeche auf ihr, damit die Maske nur den Text ausblendet.
 *
 * @param {HTMLTextAreaElement} feld
 */
function pflegeRollrand(feld) {
  const rollt = feld.classList.contains('dp-perso__eingabe--rollt');
  feld.classList.toggle('dp-perso__eingabe--rollt-oben', rollt && feld.scrollTop > 2);
  feld.classList.toggle('dp-perso__eingabe--rollt-unten', rollt && feld.scrollTop + feld.clientHeight < feld.scrollHeight - 2);
  const huelle = feld.closest('.dp-perso__feld');
  if (huelle) huelle.classList.toggle('dp-perso__feld--rollt', rollt);
}

/* scroll steigt nicht auf - mitgehoert wird deshalb auf dem Weg hinunter. */
document.addEventListener(
  'scroll',
  (ereignis) => {
    const ziel = ereignis.target;
    if (ziel instanceof HTMLTextAreaElement && istPersoFeld(ziel)) pflegeRollrand(ziel);
  },
  { capture: true, passive: true }
);

/*
 * Nicht nur beim Tippen: Ein Feld, das schon Text mitbringt (Vorgabe oder
 * wiederhergestellte Eingabe), meldet sich mit dp:hoehe, und beim Oeffnen
 * eines Reiters wird nachgemessen, was vorher verborgen war.
 */
document.addEventListener('dp:hoehe', (ereignis) => {
  if (ereignis.target instanceof HTMLTextAreaElement) passeHoeheAn(ereignis.target);
});
document.addEventListener(
  'toggle',
  (ereignis) => {
    const ziel = ereignis.target;
    if (!(ziel instanceof HTMLDetailsElement) || !ziel.open) return;
    for (const feld of ziel.querySelectorAll('textarea')) if (istPersoFeld(feld) && feld.value) passeHoeheAn(feld);
  },
  true
);

document.addEventListener('input', (ereignis) => {
  const ziel = ereignis.target;
  if (!istPersoFeld(ziel)) return;

  if (ziel instanceof HTMLTextAreaElement) passeHoeheAn(ziel);

  normalisiere(ziel);
  // Wer anfaengt zu schreiben (oder den Haken setzt), hat den Hinweis verstanden.
  if (!istLeer(ziel)) markiere(ziel, false);
});

/*
 * Ankreuzfelder melden ihre Wahl als change - auch dort verschwindet die rote
 * Markierung, sobald gewaehlt ist.
 */
document.addEventListener('change', (ereignis) => {
  const ziel = ereignis.target;
  if (!istPersoFeld(ziel)) return;
  if (!istLeer(ziel)) markiere(ziel, false);
});

/*
 * Vor jedem Klick und vor dem Absenden noch einmal ueber alle Felder: sonst
 * blieben solche uebrig, in die nie jemand getippt hat - etwa gleich nach einem
 * Variantenwechsel, bei dem das Theme das Formular neu zeichnet.
 */
document.addEventListener('click', alleFelder, true);
document.addEventListener('submit', alleFelder, true);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', alleFelder, { once: true });
} else {
  alleFelder();
}

/*
 * Pflichtangaben durchsetzen.
 *
 * Das Theme schickt das Formular nicht auf dem ueblichen Weg ab, sondern liest
 * es beim Klick auf den Knopf selbst aus - die eingebaute Pruefung des Browsers
 * kaeme dabei nie zum Zuge. Deshalb wird hier geprueft, bevor der Klick
 * ueberhaupt beim Theme ankommt.
 *
 * Ist die Gruppe zugeklappt, geht sie auf: sonst zeigte der Browser seine
 * Meldung an einem Feld, das gar nicht zu sehen ist.
 *
 * Der Horcher haengt am Fenster, nicht am Dokument: Die Einfangphase des
 * Fensters laeuft vor der des Dokuments, unabhaengig von jeder Reihenfolge
 * der Anmeldungen - kein anderer Horcher kommt ihm zuvor.
 */
/**
 * Prueft, ob dem Kauf noch etwas fehlt - und zeigt es an.
 *
 * Gibt zurueck, ob der Kauf aufzuhalten ist. Aufgerufen wird sie von zwei
 * Seiten: vom Klick auf den Knopf und vom Absenden des Formulars. Der zweite
 * Weg ist keine Feinheit - die Eingabetaste in einem Textfeld schickt ein
 * Formular ab, ohne dass jemand den Knopf beruehrt haette.
 *
 * @param {Element} knopf
 * @param {HTMLFormElement} formular
 * @returns {boolean}
 */
function haltZurueck(knopf, formular) {
  /*
   * Ueber die Elementliste des Formulars und nicht ueber seine Kinder: die
   * Felder stehen weiter oben im Kauffenster und gehoeren dem Formular nur
   * ueber ihr form-Attribut an. Im Baum darunter waeren sie nicht zu finden.
   */
  const fehlend = [...formular.elements].find((feld) => istPersoFeld(feld) && istPflicht(feld) && istLeer(feld));

  /*
   * Auch die offene Farbwahl haelt zurueck. Sonst legte die Eingabetaste
   * einen Artikel in den Warenkorb, dessen Farbe niemand gewaehlt hat.
   */
  const ohneFarbe = knopf.classList.contains('dp-kaufknopf--ohne-farbe');

  if (!fehlend && !ohneFarbe) return false;

  if (fehlend) meldeFehlendes(formular, fehlend);
  if (ohneFarbe) document.dispatchEvent(new CustomEvent('dp:farbe-anmahnen'));

  return true;
}

window.addEventListener(
  'click',
  (ereignis) => {
    const ziel = ereignis.target;
    if (!(ziel instanceof Element)) return;

    const knopf = ziel.closest('add-to-cart-component button');
    if (!knopf) return;

    const formular = knopf.closest('form');
    if (!formular) return;

    if (!haltZurueck(knopf, formular)) return;

    ereignis.preventDefault();
    // Die harte Form: auch spaeter angemeldete Horcher am Dokument bleiben aussen vor.
    ereignis.stopImmediatePropagation();
  },
  true
);

/*
 * Derselbe Halt beim Absenden des Formulars - der Weg ueber die
 * Eingabetaste. Das eigene Absenden mit Datei (weiter unten) laeuft ueber
 * formular.submit() und loest dieses Ereignis gar nicht erst aus.
 */
window.addEventListener(
  'submit',
  (ereignis) => {
    const formular = ereignis.target;
    if (!(formular instanceof HTMLFormElement)) return;

    const knopf = formular.querySelector('add-to-cart-component button');
    if (!knopf) return;

    if (!haltZurueck(knopf, formular)) return;

    ereignis.preventDefault();
    ereignis.stopImmediatePropagation();
  },
  true
);

/**
 * Oeffnet den Reiter und markiert die fehlenden Pflichtfelder.
 *
 * @param {HTMLFormElement} formular
 * @param {Element} fehlend Das erste Feld ohne Angabe.
 */
function meldeFehlendes(formular, fehlend) {
  /*
   * Geoeffnet wird ueber den Reiter, nicht am Aufklappfeld selbst. Das
   * direkte Setzen von open liess auf dem Handy die Beschreibung offen
   * stehen, und das rot markierte Feld tauchte ohne Platzmass hinter
   * Kaufknopf und Mengenwahl auf. Der Reiterknopf macht es richtig: er
   * schliesst das andere Feld, misst den Platz und merkt die Wahl als
   * Nutzerentscheidung an. Geklickt wird nur bei zugeklapptem Feld - bei
   * offenem wuerde der Knopf es wieder schliessen. Wo kein Reiter steht,
   * bleibt der alte Weg.
   */
  const feldgruppe = fehlend.closest('details');
  if (feldgruppe && !feldgruppe.open) {
    const reiter = document.querySelector('.dp-reiter__knopf--perso');
    if (reiter) {
      reiter.click();
    } else {
      feldgruppe.open = true;
    }
  }

  /*
   * Markiert wird am Feld selbst, nicht ueber die Meldung des Browsers.
   *
   * Dessen kleines Fenster steht in der Sprache des Browsers - bei vielen
   * Besuchern also auf Englisch - und laesst sich weder uebersetzen noch
   * gestalten. Ein roetlicher Rahmen sagt dasselbe, ohne aus dem Bild zu
   * fallen.
   */
  for (const feld of formular.elements) {
    if (!istPersoFeld(feld) || !istPflicht(feld)) continue;
    markiere(feld, istLeer(feld));
  }

  /*
   * Der Fokus bleibt, wo er ist. Auf das erste fehlende Feld gesetzt, fuhr
   * auf dem Handy die Tastatur hoch, ohne dass jemand das Feld angetippt
   * haette - der halbe Bildschirm war weg. Die rote Markierung sagt schon,
   * was fehlt; wer mit der Tastatur arbeitet, findet es ueber aria-invalid
   * (siehe markiere).
   */
}

/*
 * Dateien reisen im gewoehnlichen Weg mit.
 *
 * Frueher wurde an dieser Stelle das ganze Formular abgeschickt - in der
 * Annahme, die Warenkorb-Anfrage des Themes nehme keine Dateien an. Sie tut
 * es doch: das Theme baut seine Anfrage aus FormData und loescht dabei die
 * Angabe zum Inhaltstyp, sodass der Browser multipart/form-data samt Grenze
 * selbst setzt. /cart/add.js legt die Datei dann auf Shopifys Ablage und
 * traegt ihre Adresse als Eigenschaft des Postens ein.
 *
 * Der alte Weg kostete beides, was den Kauf angenehm macht: den Ladezustand
 * am Knopf und die Produktseite. Er wechselte zur Seite /cart, statt die
 * Schublade zu oeffnen.
 *
 * Was der Weg ueber die Anfrage im Hintergrund nicht mitbringt, ist die
 * Rueckmeldung waehrend des Wartens: das Theme spielt seine Bestaetigung am
 * Knopf sofort ab, die Datei ist aber erst Sekunden spaeter oben. Deshalb
 * dreht sich hier so lange ein Ring auf dem Knopf.
 */

/** Klasse fuer den drehenden Ring (Stilblatt). */
const LADE_KLASSE = 'dp-kaufknopf--laedt';

/** Nach dieser Zeit hoert der Ring von selbst auf - falls nichts zurueckkommt. */
const LADE_HOECHSTENS = 45000;

/**
 * Laesst den Knopf laden, bis die Warenkorb-Anfrage beantwortet ist.
 *
 * Das Ende meldet die Leistungsuhr des Browsers: jede Anfrage hinterlaesst
 * dort einen Eintrag, sobald ihre Antwort vollstaendig da ist. Das ist
 * genauer als jedes Nachfragen im Takt und kostet keine eigene Anfrage.
 *
 * @param {HTMLElement} knopf
 */
function begleiteHochladen(knopf) {
  if (knopf.dataset.dpLaedt !== undefined) return;
  knopf.dataset.dpLaedt = '';
  knopf.classList.add(LADE_KLASSE);
  // Der Kreis im Kaufknopf fuellt sich mit dem Hochladen der Dateien (dp-kauf-fortschritt.js).
  starteFortschritt('personalisierung', undefined, { kurz: true });
  schritt('hochladen');

  let wecker = 0;
  let beobachter = null;

  /**
   * @param {boolean} [gelungen] - false, wenn der Warenkorb abgelehnt hat:
   *   dann kein Haken, die Meldung dazu zeigt dp-meldung.js.
   */
  const fertig = (gelungen = true, abgelaufen = false) => {
    window.clearTimeout(wecker);
    if (beobachter) beobachter.disconnect();
    delete knopf.dataset.dpLaedt;
    if (!gelungen) {
      knopf.classList.remove(LADE_KLASSE);
      beendeFortschritt();
      return;
    }
    // Erst laeuft der Kreis voll, dann kommt die Bestaetigung.
    vollendeFortschritt({ melden: !abgelaufen }).then(() => {
      knopf.classList.remove(LADE_KLASSE);
      zeigeHaken();
    });
  };

  const zeigeHaken = () => {
    /*
     * Die Bestaetigung des Themes lief waehrend des Wartens verdeckt ab und
     * war laengst vorbei, als der Posten wirklich im Korb lag. Hier wird sie
     * noch einmal angestossen - der Haken kommt also nach dem Ring, nicht
     * vor ihm.
     */
    if (knopf.isConnected) {
      knopf.removeAttribute('data-added');
      void knopf.offsetWidth;
      knopf.setAttribute('data-added', 'true');
      window.setTimeout(() => {
        if (knopf.isConnected) knopf.removeAttribute('data-added');
      }, 1800);
    }
  };

  if (typeof PerformanceObserver === 'function') {
    try {
      beobachter = new PerformanceObserver((liste) => {
        for (const eintrag of liste.getEntries()) {
          if (eintrag.name && eintrag.name.includes('/cart/add')) {
            /*
             * Der Statuscode steht am Eintrag (Chrome, Firefox; Safari
             * nennt 0): ab 400 hat der Korb abgelehnt - etwa 413 bei zu
             * grossen Bildern -, dann kein Haken.
             */
            const status = 'responseStatus' in eintrag ? Number(eintrag.responseStatus) : 0;
            fertig(!(status >= 400));
            return;
          }
        }
      });
      beobachter.observe({ type: 'resource', buffered: false });
    } catch {
      beobachter = null;
    }
  }

  wecker = window.setTimeout(() => fertig(true, true), beobachter ? LADE_HOECHSTENS : 2500);
}

/*
 * Der Horcher steht hinter der Pflichtpruefung: haelt die den Klick auf,
 * kommt es hier gar nicht erst an - sie beendet die Weitergabe hart.
 */
window.addEventListener(
  'click',
  (ereignis) => {
    const ziel = ereignis.target;
    if (!(ziel instanceof Element)) return;

    const knopf = ziel.closest('add-to-cart-component button');
    const formular = knopf?.closest('form');
    if (!(knopf instanceof HTMLElement) || !formular) return;
    // IMPRINT, SOLID und LAYER begleiten ihren Kauf selbst - sonst liefen zwei Begleitungen.
    if (knopf.closest('.dp-kauffenster--imprint, .dp-kauffenster--solid, .dp-kauffenster--transfer')) return;

    // Eigene Dateifelder schicken ihre Datei mit dem Formular - das braucht seine Zeit.
    const mitDatei = [...formular.elements].some(
      (feld) =>
        feld instanceof HTMLInputElement &&
        feld.matches('[data-dp-datei]') &&
        feld.files &&
        feld.files.length > 0
    );
    if (!mitDatei) return;

    begleiteHochladen(knopf);
  },
  true
);

/*
 * Ist eine Datei gewaehlt, steht ihr Name im Feld.
 */
document.addEventListener('change', (ereignis) => {
  const ziel = ereignis.target;
  if (!(ziel instanceof HTMLInputElement) || !ziel.matches('[data-dp-datei]')) return;

  normalisiere(ziel);

  const marke = ziel.parentElement && ziel.parentElement.querySelector('[data-dp-dateimarke]');
  if (!marke) return;

  const datei = ziel.files && ziel.files[0];
  if (datei) {
    marke.textContent = datei.name;
    marke.classList.add('dp-perso__marke--gewaehlt');
    markiere(ziel, false);
  } else {
    marke.classList.remove('dp-perso__marke--gewaehlt');
  }
  // Mit Datei steht der Name auf dem Rand (Stilblatt: Der Feldname auf dem Rand).
  ziel.parentElement.classList.toggle('dp-perso__feld--belegt', !!datei);
});

/**
 * Setzt oder loescht die Markierung an einem Feld.
 *
 * @param {HTMLInputElement} feld
 * @param {boolean} fehlt
 */
function markiere(feld, fehlt) {
  feld.classList.toggle('dp-perso__eingabe--fehlt', fehlt);
  if (feld.matches('[data-dp-datei]')) feld.classList.toggle('dp-perso__datei--fehlt', fehlt);
  if (fehlt) {
    feld.setAttribute('aria-invalid', 'true');
  } else {
    feld.removeAttribute('aria-invalid');
  }

  /*
   * Der Traeger bekommt den Zustand als Klasse mit - das Stilblatt las ihn
   * frueher ueber :has() vom Feld ab, und genau diese Selektoren machten die
   * Reiter-Animation auf dem Handy zaeh.
   */
  const dateiFeld = feld.closest('.dp-perso__feld--datei');
  if (dateiFeld) {
    traegerKlasse(dateiFeld, 'dp-feld--fehlt', !!dateiFeld.querySelector('.dp-perso__datei--fehlt'));
  }
}

/**
 * Setzt eine Klasse nur bei Abweichung - haelt Beobachter still.
 *
 * @param {Element} el
 * @param {string} klasse
 * @param {boolean} wert
 */
function traegerKlasse(el, klasse, wert) {
  if (el.classList.contains(klasse) !== wert) el.classList.toggle(klasse, wert);
}

/*
 * Aus dem Warenkorb-Knopf wird ein Knopf zum Personalisieren, solange noch
 * eine Pflichtangabe fehlt.
 *
 * Der Knopf tut in diesem Zustand ohnehin schon etwas anderes: die
 * Pflichtpruefung weiter oben faengt den Klick ab, oeffnet den Reiter und
 * markiert die fehlenden Felder. Bis jetzt sah man ihm das nicht an - er
 * bot den Kauf an und lieferte etwas anderes. Jetzt zeigt er einen Stift,
 * und das Wort fuer die Vorlesehilfe wechselt mit.
 *
 * Gewechselt wird nur bei Abweichung: die Funktion laeuft bei jeder
 * Aenderung im Kauffenster mit.
 */
const PERSO_WORT = 'Personalisierung ausfüllen';
const FARB_WORT = 'Erst eine Farbe wählen';

function pflegeKaufKnopf() {
  const knopf = document.querySelector('.dp-kauffenster add-to-cart-component button');
  if (!(knopf instanceof HTMLElement)) return;

  const formular = knopf.closest('form');
  if (!formular) return;

  const fehlt = [...formular.elements].some((feld) => istPersoFeld(feld) && istPflicht(feld) && istLeer(feld));

  /*
   * Gesperrt ist nicht gleich gesperrt. Ausverkauft heisst, es gibt nichts
   * zu holen - dort bleibt der durchgestrichene Kreis stehen. Fehlt dagegen
   * nur die Farbwahl (Marke aus dp-wahlwache.js), ist der Artikel zu haben,
   * und die fehlende Personalisierung darf sich schon zeigen.
   */
  const nurFarbeFehlt = knopf.classList.contains('dp-kaufknopf--ohne-farbe');

  /*
   * Der Stift steht fuer alles, was der Kunde noch beisteuern muss - die
   * Personalisierung ebenso wie die offene Farbwahl. Ein Artikel, der nur
   * eine Farbwahl hat, zeigte sonst den durchgestrichenen Kreis, als waere
   * er ausverkauft; zu tun bleibt aber etwas, und der Knopf sagt jetzt was.
   */
  const zeigen = nurFarbeFehlt || (fehlt && !knopf.disabled);
  // Welche Wahl offen ist, nennt dp-wahlwache.js am Knopf ("Erst eine Größe wählen", "Erst eine Farbe wählen").
  const wort = fehlt ? PERSO_WORT : knopf.dataset.dpWahlWort || FARB_WORT;

  if (knopf.classList.contains('dp-kaufknopf--perso') === zeigen) {
    if (zeigen && knopf.getAttribute('aria-label') !== wort) {
      knopf.setAttribute('aria-label', wort);
      knopf.setAttribute('title', wort);
    }
    return;
  }

  knopf.classList.toggle('dp-kaufknopf--perso', zeigen);

  if (zeigen) {
    knopf.setAttribute('aria-label', wort);
    knopf.setAttribute('title', wort);
  } else {
    /*
     * Ohne aria-label gilt wieder der Text im Knopf ("In den Warenkorb
     * legen") - der steht weiterhin im Markup und wird nur nicht gezeichnet.
     */
    knopf.removeAttribute('aria-label');
    knopf.removeAttribute('title');
  }
}

/**
 * Markiert den Kasten, in dem die Personalisierung steht (Stilblatt,
 * dp-hat-perso) - und pflegt den Kaufknopf.
 */
function pflegeKauffenster() {
  const perso = document.querySelector('.dp-kauffenster accordion-custom .dp-perso');
  if (perso) {
    const kasten = perso.closest('accordion-custom');
    if (kasten) traegerKlasse(kasten, 'dp-hat-perso', true);
  }
  pflegeKaufKnopf();
}

/*
 * Eingaben aendern nichts am Baum - der Beobachter unten sieht sie nicht.
 * Deshalb hier zusaetzlich auf die Ereignisse hoeren, mit denen sich der
 * Zustand der Pflichtfelder aendern kann.
 */
for (const art of ['input', 'change']) {
  document.addEventListener(art, pflegeKaufKnopf, true);
}

// Die Farbwache (dp-wahlwache.js) meldet, wenn sie den Knopf nach der Farbwahl freigibt.
document.addEventListener('dp:kaufknopf-pruefen', pflegeKaufKnopf);

/*
 * Laeuft dieses Skript, prueft es den Kauf samt offener Farbwahl selbst
 * (haltZurueck) - die Farbwache haelt sich dann heraus.
 */
/** @type {any} */ (window).dpPersoWache = true;

/*
 * Beim Aufbau greifen mehrere Skripte nacheinander in den Knopf: das Theme
 * gibt ihn frei, die Farbwache sperrt ihn wieder und setzt ihre Marke. Wer
 * nur einmal nachsieht, trifft womoeglich einen Zwischenstand. Ein paar
 * kurze, begrenzte Blicke - die Funktion selbst schreibt nur bei Abweichung.
 */
for (const wann of [0, 200, 600, 1200, 2500]) {
  window.setTimeout(pflegeKauffenster, wann);
}

/*
 * Zeichnet das Theme das Kauffenster neu (Variantenwechsel), kommt ein
 * frischer Knopf ohne Marken. Beobachtet wird nur das Kauffenster selbst,
 * nicht das ganze Dokument - jede Aenderung irgendwo auf der Produktseite
 * liefe sonst mit durch.
 */
const kauffenster = document.querySelector('.dp-kauffenster');
if (kauffenster) {
  let geplant = false;
  new MutationObserver(() => {
    if (geplant) return;
    geplant = true;
    requestAnimationFrame(() => {
      geplant = false;
      pflegeKauffenster();
    });
  }).observe(kauffenster, { childList: true, subtree: true });
}
